/**
 * Mammoth (and many HTML docs) wrap images in <p><img/></p>.
 * BlockNote only creates image blocks from top-level <img> / <figure>,
 * so nested imgs are dropped — lift them out before parsing.
 */
export function promoteImagesInHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const images = Array.from(doc.body.querySelectorAll("img"));

  for (const img of images) {
    const parent = img.parentElement;
    if (!parent || parent === doc.body) continue;
    if (parent.tagName === "FIGURE") continue;

    const figure = doc.createElement("figure");
    figure.appendChild(img.cloneNode(true));

    const siblings = Array.from(parent.childNodes).filter((node) => {
      if (node === img) return false;
      if (node.nodeType === Node.TEXT_NODE) {
        return Boolean(node.textContent?.trim());
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        return el.tagName !== "BR";
      }
      return true;
    });

    if (siblings.length === 0) {
      parent.replaceWith(figure);
    } else {
      parent.parentNode?.insertBefore(figure, parent.nextSibling);
      img.remove();
    }
  }

  return doc.body.innerHTML;
}

/** Drop video/embed media from imported HTML. Images are kept. */
export function stripUnsupportedMediaFromHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("video, audio, source, embed, object, iframe")
    .forEach((node) => node.remove());

  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    const isVideo =
      src.startsWith("data:video") ||
      /\.(mp4|mov|webm|avi|mkv|m4v)(\?|#|$)/i.test(src);
    if (!src || isVideo) img.remove();
  });

  return doc.body.innerHTML;
}

export function prepareImportedHtml(html: string): string {
  return promoteImagesInHtml(stripUnsupportedMediaFromHtml(html));
}
